<!--suppress HtmlUnknownTag IntelliJ incorrectly flags oc- tags as unknown -->
<script lang="ts">
    import {mount, onMount, unmount} from "svelte";
    import {type CustomerSessionRefreshed, onCustomerSessionRefresh} from "@otto-ec/fti-customer-session-frontend";
    import type {Unset} from "@otto-ec/global-resources/nexus";
    import {determinePageCluster} from "../../tracking/pageCluster";
    import {trackOnNextPageImpression} from "@otto-ec/tracking-bct";
    import LoggedInBadge from "./LoggedInBadge.svelte";
    import { toggle } from "@otto-ec/global-resources/toggle";

    interface CsrfTokenResponse {
        token: string;
        headerName: string;
        parameterName: string;
    }

    export async function fetchCsrfToken(): Promise<CsrfTokenResponse> {
        const response = await fetch("/customer-identity/rest/csrf-token", {
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error(`[logout] CSRF token fetch failed: HTTP ${response.status}`);
        }

        return response.json() as Promise<CsrfTokenResponse>;
    }

    export async function performLogout(logOutUrl: string, csrfToken: CsrfTokenResponse): Promise<void> {
        const response = await fetch(logOutUrl, {
            method: "POST",
            credentials: "include",
            headers: {
                [csrfToken.headerName]: csrfToken.token,
            },
        });

        if (!response.ok) {
            throw new Error(`[logout] Logout failed: HTTP ${response.status}`);
        }

        window.location.assign(response.url);
    }

    interface Props {
        label?: string;
    }

    let {label = "Mein Konto"}: Props = $props();
    let isTooltipDisabled = $state(true);
    let isLoggedIn = $state(false);
    type ResizeCallback = (entry: ResizeObserverEntry | undefined) => void;

    const onLabelResize: ResizeCallback = (entry) => {
        if (entry) {
            isTooltipDisabled = entry.contentRect.width >= entry.target.scrollWidth;
        }
    };

    onMount((): Unset => {
        const flyoutLabel = document.querySelector<HTMLElement>(".fti-fly-js-label");
        let resizeObserver: ResizeObserver | undefined;
        if (flyoutLabel) {
            resizeObserver = new ResizeObserver(([entry]) => {
                onLabelResize(entry)
            });
            resizeObserver.observe(flyoutLabel);
        }


        const unsubscribe = onCustomerSessionRefresh.subscribe((refreshed: CustomerSessionRefreshed) => {
            isLoggedIn = refreshed.hasCustomerSession;
            updateForLoginState(isLoggedIn);

            const staticOverlay = document.querySelector<HTMLElement>(".fti-fly-js-icon-overlay");
            if (staticOverlay) {
                staticOverlay.setAttribute(
                    "oc-aria-label",
                    `${label}${isLoggedIn ? " - Angemeldet" : ""}`
                );
            }
        });

        return () => {
            resizeObserver?.disconnect();
            unsubscribe();
        };
    });

    $effect(() => {
        const container = document.querySelector<HTMLElement>(".fti-fly-js-icon-container");
        if (!container) return;

        if (isLoggedIn) {
            const badge = mount(LoggedInBadge, {target: container});

            return () => {
                void unmount(badge);
            };
        }
    });

    function updateForLoginState(isLoggedIn: boolean) {
        const actionsContainer = document.querySelector<HTMLElement>(".fti-fly-js-actions");
        if (!actionsContainer) return;

        if (isLoggedIn) {
            actionsContainer.innerHTML = `
                <oc-button-v1 variant="secondary" data-qa="fti-fly-logout-button"
                              data-tr-v1=""
                              data-tr-v1-click='{"user_LogoutActivity": ["click"], "user_LogoutSource": ["Flyout"]}'
                              data-tr-v1.track="click">
                    <a class="fti-fly-js-logout-link" href="/customer-identity/logout" rel="nofollow">Abmelden</a>
                </oc-button-v1>
            `;
            const logoutButton = actionsContainer.querySelector("[data-qa=fti-fly-logout-button]");
            if (logoutButton) {
                logoutButton.addEventListener('click', () => {
                    void trackOnNextPageImpression({
                        aftersales_NaviDestination: ["Logout"],
                        aftersales_NaviSource: [determinePageCluster() || ""],
                        aftersales_NaviType: ["Flyout"]
                    });
                });
            }
            const logoutLink = actionsContainer.querySelector<HTMLAnchorElement>(".fti-fly-js-logout-link");
            if (logoutLink) {
                logoutLink.addEventListener("click", (event) => {
                    event.preventDefault();
                    if (toggle.get("fti_enable_logout_with_csrf", false)) {
                        void (async () => {
                            try {
                                const csrfToken = await fetchCsrfToken();
                                await performLogout("/customer-identity/esi-logout", csrfToken);
                            } catch (err) {
                                console.error("[logout] Failed to log out", err);
                            }
                        })();
                    } else {
                        window.location.href = logoutLink.href;
                    }
                });
            }
        } else {
            const pageCluster = determinePageCluster();
            actionsContainer.innerHTML = `
                <oc-button-v1 base64-href="L2N1c3RvbWVyLWlkZW50aXR5L2xvZ2luP2VudHJ5UG9pbnQ9bG9naW5BcmVh"
                              variant="primary" data-qa="fti-fly-login-button"
                              data-tr-v1=""
                              data-tr-v1-tr-next-page-impression='${JSON.stringify({
                aftersales_NaviDestination: ["Login"],
                aftersales_NaviSource: [pageCluster],
                aftersales_NaviType: ["Flyout"]
            })}'>Anmelden</oc-button-v1>
                <oc-button-v1 base64-href="L2N1c3RvbWVyLWlkZW50aXR5L3N0YXJ0UmVnaXN0cmF0aW9uP2VudHJ5UG9pbnQ9bG9naW5BcmVh"
                              variant="tertiary" data-qa="fti-fly-registration-button"
                              data-tr-v1=""
                              data-tr-v1-tr-next-page-impression='${JSON.stringify({
                aftersales_NaviDestination: ["CustomerCreation"],
                aftersales_NaviSource: [pageCluster],
                aftersales_NaviType: ["Flyout"]
            })}'>Neu bei OTTO? Jetzt registrieren</oc-button-v1>
            `;
        }
    }

</script>

<oc-popover-v1 position="bottom" anchor="#fti-fly-icon-overlay" class="fti-fly-tooltip" data-qa="fti-fly-tooltip"
               variant="tooltip"
               trigger={isTooltipDisabled ? "none" : "mouseenter"}>
    {label}
</oc-popover-v1>
